import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Clock,
  MapPin,
  Trash2,
  Pencil,
  Download,
  Check,
  Ban,
  User as UserIcon,
} from "lucide-react";

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WEEK_DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const TIPOS = ["Reunião", "Visita", "Assembleia", "Outro"];

const KIND_STYLE = {
  evento: { bg: "bg-[#0047FF]/10", text: "text-[#0047FF]", label: "Evento" },
  servico: { bg: "bg-[#15803D]/10", text: "text-[#15803D]", label: "Serviço" },
  orcamento: { bg: "bg-[#B45309]/10", text: "text-[#B45309]", label: "Orçamento" },
  consulta: { bg: "bg-[#7C3AED]/10", text: "text-[#7C3AED]", label: "Consulta" },
};

const ESTADO_STYLE = {
  Pendente: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300" },
  Aceite: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  Recusada: { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300" },
};

function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function startOfMonthGrid(ref) {
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const jsDow = first.getDay();
  const offset = (jsDow + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  return start;
}

const EMPTY = {
  titulo: "",
  data: "",
  hora_inicio: "",
  hora_fim: "",
  local: "",
  descricao: "",
  tipo: "Reunião",
  condominio_id: "",
  user_id: "",
};

export default function Agenda() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const myId = user?.id;

  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [weekAnchor, setWeekAnchor] = useState(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    const offset = (t.getDay() + 6) % 7;
    t.setDate(t.getDate() - offset);
    return t;
  });
  const [filterUser, setFilterUser] = useState(""); // admin filter
  const [workers, setWorkers] = useState([]);
  const [items, setItems] = useState([]);
  const [condominios, setCondominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const gridStart = useMemo(
    () => (view === "week" ? new Date(weekAnchor) : startOfMonthGrid(cursor)),
    [cursor, weekAnchor, view]
  );
  const gridEnd = useMemo(() => {
    const e = new Date(gridStart);
    e.setDate(e.getDate() + (view === "week" ? 6 : 41));
    return e;
  }, [gridStart, view]);

  const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const weekLabel = useMemo(() => {
    const end = new Date(weekAnchor);
    end.setDate(end.getDate() + 6);
    const sameYear = weekAnchor.getFullYear() === end.getFullYear();
    return `${weekAnchor.getDate()} ${MONTH_NAMES[weekAnchor.getMonth()].slice(0, 3)}${sameYear ? "" : " " + weekAnchor.getFullYear()} – ${end.getDate()} ${MONTH_NAMES[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`;
  }, [weekAnchor]);

  async function refresh() {
    setLoading(true);
    try {
      const params = { from: iso(gridStart), to: iso(gridEnd) };
      if (isAdmin && filterUser) params.user_id = filterUser;
      const requests = [api.get("/agenda", { params }), api.get("/condominios")];
      if (isAdmin && workers.length === 0) requests.push(api.get("/users/workers"));
      const results = await Promise.all(requests);
      setItems(results[0].data.items || []);
      setCondominios(results[1].data);
      if (results[2]) setWorkers(results[2].data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, weekAnchor, view, filterUser, isAdmin]);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      map[it.data] = map[it.data] || [];
      map[it.data].push(it);
    }
    // Sort each day's items by hora_inicio
    for (const key in map) {
      map[key].sort((a, b) => (a.hora_inicio || "99:99").localeCompare(b.hora_inicio || "99:99"));
    }
    return map;
  }, [items]);

  const days = useMemo(() => {
    const total = view === "week" ? 7 : 42;
    const arr = [];
    for (let i = 0; i < total; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [gridStart, view]);

  function navigate(dir) {
    if (view === "week") {
      const nw = new Date(weekAnchor);
      nw.setDate(nw.getDate() + dir * 7);
      setWeekAnchor(nw);
    } else {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
    }
  }

  function goToday() {
    const t = new Date();
    if (view === "week") {
      t.setHours(0, 0, 0, 0);
      const offset = (t.getDay() + 6) % 7;
      t.setDate(t.getDate() - offset);
      setWeekAnchor(t);
    } else {
      setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    }
  }

  function openCreateOn(dateIso) {
    setEditing(null);
    setForm({ ...EMPTY, data: dateIso, user_id: isAdmin ? "" : myId || "" });
    setSelectedDay(null);
    setOpenForm(true);
  }

  function openEdit(ev) {
    setEditing(ev);
    setForm({
      titulo: ev.titulo || "",
      data: ev.data || "",
      hora_inicio: ev.hora_inicio || "",
      hora_fim: ev.hora_fim || "",
      local: ev.local || "",
      descricao: ev.descricao || "",
      tipo: ev.tipo || "Reunião",
      condominio_id: ev.condominio_id || "",
      user_id: ev.user_id || "",
    });
    setSelectedDay(null);
    setOpenForm(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.titulo.trim() || !form.data) {
      toast.error("Título e data são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        condominio_id: form.condominio_id || null,
        user_id: form.user_id || null,
      };
      if (editing) {
        await api.put(`/eventos/${editing.id}`, payload);
        toast.success("Marcação atualizada");
      } else {
        const r = await api.post("/eventos", payload);
        toast.success(
          r.data.estado === "Pendente"
            ? `Marcação criada e enviada a ${r.data.user_name || "trabalhador"} para aceitar`
            : "Marcação criada"
        );
      }
      setOpenForm(false);
      setEditing(null);
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(ev) {
    if (!window.confirm("Eliminar esta marcação?")) return;
    try {
      await api.delete(`/eventos/${ev.id}`);
      toast.success("Marcação eliminada");
      setSelectedDay(null);
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  async function onAccept(ev) {
    try {
      await api.post(`/eventos/${ev.id}/accept`);
      toast.success("Marcação aceite");
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  async function onDecline(ev) {
    if (!window.confirm("Recusar esta marcação? Vai desaparecer da sua agenda.")) return;
    try {
      await api.post(`/eventos/${ev.id}/decline`);
      toast.success("Marcação recusada");
      setSelectedDay(null);
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  async function onExportIcal() {
    try {
      const token = localStorage.getItem("crm_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/agenda/ical?from=${iso(gridStart)}&to=${iso(gridEnd)}&token=${encodeURIComponent(token || "")}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `agenda_${iso(gridStart)}_${iso(gridEnd)}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Ficheiro iCal gerado");
    } catch (e) {
      toast.error(e.message || "Falha a exportar iCal");
    }
  }

  const today = iso(new Date());
  const selectedItems = selectedDay ? byDay[selectedDay] || [] : [];
  const pendingCount = items.filter((it) => it.kind === "evento" && it.estado === "Pendente" && it.user_id === myId).length;

  return (
    <div className="space-y-6" data-testid="agenda-page">
      {/* Pending inbox for current user */}
      {pendingCount > 0 && (
        <div
          data-testid="agenda-pending-banner"
          className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="font-manrope text-sm text-amber-900">
            Tem <b>{pendingCount}</b> marcaç{pendingCount === 1 ? "ão" : "ões"} pendente{pendingCount === 1 ? "" : "s"} para aceitar ou recusar.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="agenda-prev" onClick={() => navigate(-1)} className="p-2 border border-zinc-200 bg-white rounded-sm hover:border-[#0047FF] hover:text-[#0047FF] transition-colors">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button data-testid="agenda-today" onClick={goToday} className="px-3 py-2 border border-zinc-200 bg-white rounded-sm font-manrope text-sm hover:border-[#0047FF] hover:text-[#0047FF] transition-colors">Hoje</button>
          <button data-testid="agenda-next" onClick={() => navigate(1)} className="p-2 border border-zinc-200 bg-white rounded-sm hover:border-[#0047FF] hover:text-[#0047FF] transition-colors">
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div data-testid="agenda-month-label" className="ml-3 font-chivo font-black text-xl text-neutral-950 tracking-tight capitalize">
            {view === "week" ? weekLabel : monthLabel}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <select
              data-testid="agenda-filter-worker"
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="bg-white border border-zinc-200 rounded-sm px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
              title="Filtrar agenda por trabalhador"
            >
              <option value="">Todos os trabalhadores</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          )}
          <div className="inline-flex border border-zinc-200 bg-white rounded-sm overflow-hidden">
            <button data-testid="agenda-view-month" onClick={() => setView("month")} className={`px-3 py-2 font-manrope text-xs transition-colors ${view === "month" ? "bg-[#0047FF] text-white" : "text-neutral-600 hover:text-[#0047FF]"}`}>Mês</button>
            <button data-testid="agenda-view-week" onClick={() => setView("week")} className={`px-3 py-2 font-manrope text-xs transition-colors ${view === "week" ? "bg-[#0047FF] text-white" : "text-neutral-600 hover:text-[#0047FF]"}`}>Semana</button>
          </div>
          <button data-testid="agenda-export-ical" onClick={onExportIcal} className="px-4 py-2 border border-zinc-200 bg-white rounded-sm font-manrope text-sm hover:border-[#0047FF] hover:text-[#0047FF] transition-colors flex items-center gap-2" title="Exportar iCal">
            <Download className="w-4 h-4" strokeWidth={1.5} />
            iCal
          </button>
          <button data-testid="agenda-new-btn" onClick={() => openCreateOn(iso(new Date()))} className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2.5 font-manrope font-semibold text-sm transition-colors flex items-center gap-2">
            <Plus className="w-4 h-4" strokeWidth={2} />
            Nova marcação
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap text-xs font-manrope">
        <span className="text-neutral-500 uppercase tracking-wider text-[10px] font-chivo font-bold">Estados:</span>
        {Object.entries(ESTADO_STYLE).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`inline-block px-2 py-0.5 text-[10px] rounded-sm border ${v.bg} ${v.text} ${v.border}`}>{k}</span>
          </div>
        ))}
        <span className="text-neutral-400 mx-2">·</span>
        <span className="text-neutral-500 uppercase tracking-wider text-[10px] font-chivo font-bold">Tipos:</span>
        {Object.entries(KIND_STYLE).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm ${v.bg}`} style={{ borderLeftWidth: 2, borderLeftColor: "currentColor" }} />
            <span className="text-neutral-600">{v.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar */}
      {view === "month" ? (
        <MonthGrid days={days} byDay={byDay} today={today} cursor={cursor} isAdmin={isAdmin} onCellClick={(iso, hasItems) => (hasItems ? setSelectedDay(iso) : openCreateOn(iso))} />
      ) : (
        <WeekView days={days} byDay={byDay} today={today} isAdmin={isAdmin} onCellClick={(d) => openCreateOn(iso(d))} onItemClick={(isoDay) => setSelectedDay(isoDay)} />
      )}

      {/* Day modal */}
      {selectedDay && (
        <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedDay(null)}>
          <div className="bg-white rounded-sm w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="agenda-day-modal">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-chivo font-black text-lg text-neutral-950">
                {new Date(selectedDay + "T12:00:00").toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </h3>
              <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
            <div className="p-6 space-y-3">
              {selectedItems.map((it) => (
                <DayItemCard
                  key={`${it.kind}-${it.id}`}
                  it={it}
                  isAdmin={isAdmin}
                  isMine={it.user_id === myId}
                  onEdit={openEdit}
                  onDelete={onDelete}
                  onAccept={onAccept}
                  onDecline={onDecline}
                />
              ))}
              <button data-testid="agenda-day-add" onClick={() => openCreateOn(selectedDay)} className="w-full border border-dashed border-zinc-300 rounded-sm px-4 py-3 font-manrope text-sm text-neutral-600 hover:border-[#0047FF] hover:text-[#0047FF] transition-colors flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Nova marcação neste dia
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      {openForm && (
        <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpenForm(false)}>
          <div className="bg-white rounded-sm w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="agenda-modal">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-chivo font-black text-lg text-neutral-950">{editing ? "Editar marcação" : "Nova marcação"}</h3>
              <button onClick={() => setOpenForm(false)} className="p-1 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-5" data-testid="agenda-form">
              <TextField label="Título / Assunto *" testid="input-titulo" value={form.titulo} onChange={(v) => setForm({ ...form, titulo: v })} required />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <TextField label="Data *" testid="input-data" type="date" required value={form.data} onChange={(v) => setForm({ ...form, data: v })} />
                <TextField label="Hora" testid="input-hora-inicio" type="time" value={form.hora_inicio} onChange={(v) => setForm({ ...form, hora_inicio: v })} />
              </div>
              <SelectField label="Tipo" testid="input-tipo" value={form.tipo} onChange={(v) => setForm({ ...form, tipo: v })} options={TIPOS.map((t) => ({ value: t, label: t }))} />
              {isAdmin && (
                <SelectField
                  label="Trabalhador responsável"
                  testid="input-user"
                  value={form.user_id || ""}
                  onChange={(v) => setForm({ ...form, user_id: v })}
                  options={[
                    { value: "", label: "— Para mim —" },
                    ...workers.map((w) => ({ value: w.id, label: `${w.name} (${w.role === "admin" ? "admin" : "user"})` })),
                  ]}
                />
              )}
              <TextField label="Local" testid="input-local" value={form.local} onChange={(v) => setForm({ ...form, local: v })} placeholder="Ex.: Edifício X, sala de reuniões" />
              <SelectField label="Condomínio (opcional)" testid="input-condominio" value={form.condominio_id || ""} onChange={(v) => setForm({ ...form, condominio_id: v })} options={[{ value: "", label: "— Nenhum —" }, ...condominios.map((c) => ({ value: c.id, label: c.nome }))]} />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">Observações / Descrição</label>
                <textarea data-testid="input-descricao" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={3} className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none resize-none" />
              </div>
              {isAdmin && form.user_id && form.user_id !== myId && !editing && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-900 font-manrope">
                  Esta marcação será criada como <b>Pendente</b> e aguarda aceitação do trabalhador escolhido.
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-200">
                <button type="button" data-testid="cancel-btn" onClick={() => setOpenForm(false)} className="px-4 py-2 border border-zinc-200 rounded-sm font-manrope text-sm hover:border-neutral-400">Cancelar</button>
                <button type="submit" data-testid="save-btn" disabled={saving} className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2 font-manrope font-semibold text-sm transition-colors disabled:opacity-60">
                  {saving ? "A guardar…" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && <div className="text-center font-manrope text-sm text-neutral-500 py-4">A carregar agenda…</div>}
    </div>
  );
}

function DayItemCard({ it, isAdmin, isMine, onEdit, onDelete, onAccept, onDecline }) {
  const kindSt = KIND_STYLE[it.kind] || KIND_STYLE.evento;
  const estadoSt = it.estado && ESTADO_STYLE[it.estado];
  return (
    <div className="border border-zinc-200 rounded-sm p-4 hover:border-[#0047FF] transition-colors" data-testid={`agenda-day-card-${it.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`inline-block text-[9px] uppercase tracking-[0.2em] font-chivo font-bold px-2 py-0.5 rounded-sm ${kindSt.bg} ${kindSt.text}`}>{kindSt.label}</span>
            {estadoSt && <span className={`inline-block text-[9px] uppercase tracking-[0.2em] font-chivo font-bold px-2 py-0.5 rounded-sm border ${estadoSt.bg} ${estadoSt.text} ${estadoSt.border}`} data-testid={`estado-${it.id}`}>{it.estado}</span>}
          </div>
          <div className="font-chivo font-black text-neutral-950 text-sm">{it.titulo}</div>
          {(isAdmin && it.user_name) && (
            <div className="text-xs text-neutral-600 font-manrope mt-1 inline-flex items-center gap-1">
              <UserIcon className="w-3 h-3" strokeWidth={1.5} />
              <span>Trabalhador: <b>{it.user_name}</b></span>
            </div>
          )}
          {it.condominio_nome && <div className="text-xs text-neutral-500 font-manrope mt-0.5">🏢 {it.condominio_nome}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {it.kind === "evento" && it.estado === "Pendente" && isMine && (
            <>
              <button data-testid={`agenda-accept-${it.id}`} onClick={() => onAccept(it)} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm text-xs font-manrope font-semibold">
                <Check className="w-3 h-3" strokeWidth={2.5} />
                Aceitar
              </button>
              <button data-testid={`agenda-decline-${it.id}`} onClick={() => onDecline(it)} className="inline-flex items-center gap-1 px-2 py-1 bg-rose-600 text-white hover:bg-rose-700 rounded-sm text-xs font-manrope font-semibold">
                <Ban className="w-3 h-3" strokeWidth={2.5} />
                Recusar
              </button>
            </>
          )}
          {it.kind === "evento" && (isAdmin || isMine) && (
            <>
              <button data-testid={`agenda-edit-${it.id}`} onClick={() => onEdit(it)} className="p-1.5 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm"><Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
              <button data-testid={`agenda-delete-${it.id}`} onClick={() => onDelete(it)} className="p-1.5 text-neutral-500 hover:text-[#FF4500] hover:bg-[#FF4500]/5 rounded-sm"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs font-manrope text-neutral-600 flex-wrap">
        {it.hora_inicio && <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} />{it.hora_inicio}{it.hora_fim ? ` – ${it.hora_fim}` : ""}</div>}
        {it.local && <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} />{it.local}</div>}
      </div>
      {it.descricao && <p className="text-xs font-manrope text-neutral-600 mt-2 whitespace-pre-wrap">{it.descricao}</p>}
    </div>
  );
}

function MonthGrid({ days, byDay, today, cursor, isAdmin, onCellClick }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      <div className="grid grid-cols-7 bg-zinc-50 border-b border-zinc-200">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const isoD = iso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = isoD === today;
          const dayItems = byDay[isoD] || [];
          const maxVisible = 3;
          return (
            <button type="button" key={i} data-testid={`agenda-cell-${isoD}`} onClick={() => onCellClick(isoD, dayItems.length > 0)} className={`min-h-[130px] text-left border-r border-b border-zinc-100 p-2 transition-colors ${inMonth ? "bg-white hover:bg-[#0047FF]/[0.03]" : "bg-zinc-50/40 text-neutral-400"} ${isToday ? "bg-[#0047FF]/5" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <div className={`font-chivo font-bold text-xs ${isToday ? "bg-[#0047FF] text-white w-6 h-6 rounded-sm flex items-center justify-center" : "text-neutral-700"}`}>{d.getDate()}</div>
                {dayItems.length > maxVisible && <span className="text-[10px] font-manrope text-neutral-400">+{dayItems.length - maxVisible}</span>}
              </div>
              <div className="space-y-0.5">
                {dayItems.slice(0, maxVisible).map((it) => {
                  const kindSt = KIND_STYLE[it.kind] || KIND_STYLE.evento;
                  const estadoDot = it.estado === "Pendente" ? "border-l-amber-500" : it.estado === "Recusada" ? "border-l-rose-500 opacity-60 line-through" : "border-l-emerald-500";
                  return (
                    <div key={`${it.kind}-${it.id}`} data-testid={`agenda-item-${it.id}`} className={`text-[11px] font-manrope rounded-sm px-1.5 py-0.5 truncate border-l-2 ${kindSt.bg} ${kindSt.text} ${it.kind === "evento" ? estadoDot : ""}`} title={`${kindSt.label}: ${it.titulo}${it.hora_inicio ? " · " + it.hora_inicio : ""}${it.user_name ? " · " + it.user_name : ""}${it.estado ? " [" + it.estado + "]" : ""}`}>
                      {it.hora_inicio && <span className="font-mono mr-1">{it.hora_inicio}</span>}
                      {it.titulo}
                      {isAdmin && it.user_name && <span className="opacity-70 ml-1">· {it.user_name.split(" ")[0]}</span>}
                    </div>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextField({ label, testid, value, onChange, type = "text", required = false, placeholder = "" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ---------- Week View ----------
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 22;
const HOUR_HEIGHT = 48;
function parseHHMM(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h + m / 60;
}
function WeekView({ days, byDay, today, isAdmin, onCellClick, onItemClick }) {
  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) hours.push(h);
  const gridHeight = (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT;
  const dayLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
  function isoOf(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="agenda-week-view">
      <div className="grid grid-cols-[60px_repeat(7,1fr)] bg-zinc-50 border-b border-zinc-200">
        <div className="px-2 py-2.5 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold text-center">HORA</div>
        {days.map((d, i) => {
          const isoD = isoOf(d);
          const isToday = isoD === today;
          return (
            <div key={i} className={`px-3 py-2.5 text-center border-l border-zinc-200 ${isToday ? "bg-[#0047FF]/5" : ""}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">{dayLabels[i]}</div>
              <div className={`font-chivo font-black text-lg mt-0.5 ${isToday ? "text-[#0047FF]" : "text-neutral-800"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-zinc-200 bg-zinc-50/40">
        <div className="px-2 py-2 text-[9px] uppercase tracking-[0.15em] text-neutral-400 font-chivo font-bold text-center">Dia todo</div>
        {days.map((d, i) => {
          const isoD = isoOf(d);
          const items = (byDay[isoD] || []).filter((x) => !x.hora_inicio);
          return (
            <div key={i} className="border-l border-zinc-200 p-1.5 space-y-1 min-h-[36px]">
              {items.map((it) => {
                const kindSt = KIND_STYLE[it.kind] || KIND_STYLE.evento;
                return (
                  <button key={`${it.kind}-${it.id}`} data-testid={`agenda-item-${it.id}`} onClick={() => onItemClick(isoD)} className={`block w-full text-left text-[11px] font-manrope rounded-sm px-1.5 py-0.5 truncate ${kindSt.bg} ${kindSt.text} hover:opacity-80`} title={`${kindSt.label}: ${it.titulo}`}>
                    {it.titulo}
                    {isAdmin && it.user_name && <span className="opacity-70"> · {it.user_name.split(" ")[0]}</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-[60px_repeat(7,1fr)] relative" style={{ height: gridHeight }}>
        <div className="relative">
          {hours.map((h, i) => i === hours.length - 1 ? null : (
            <div key={h} style={{ top: (h - DAY_START_HOUR) * HOUR_HEIGHT }} className="absolute left-0 right-0 px-2 text-[10px] font-mono text-neutral-400">{String(h).padStart(2, "0")}:00</div>
          ))}
        </div>
        {days.map((d, dayIdx) => {
          const isoD = isoOf(d);
          const items = (byDay[isoD] || []).filter((x) => x.hora_inicio);
          const isToday = isoD === today;
          return (
            <div key={dayIdx} className={`relative border-l border-zinc-200 ${isToday ? "bg-[#0047FF]/5" : ""}`} onClick={() => onCellClick(d)}>
              {hours.map((h) => (
                <div key={h} style={{ top: (h - DAY_START_HOUR) * HOUR_HEIGHT }} className="absolute left-0 right-0 border-t border-zinc-100" />
              ))}
              {items.map((it) => {
                const start = parseHHMM(it.hora_inicio);
                const end = parseHHMM(it.hora_fim) || (start !== null ? start + 1 : null);
                if (start === null || start < DAY_START_HOUR || start >= DAY_END_HOUR) return null;
                const top = (start - DAY_START_HOUR) * HOUR_HEIGHT;
                const height = Math.max(22, Math.min(gridHeight - top, (end - start) * HOUR_HEIGHT - 2));
                const kindSt = KIND_STYLE[it.kind] || KIND_STYLE.evento;
                const estadoLeftBorder = it.estado === "Pendente" ? "border-l-amber-500" : it.estado === "Recusada" ? "border-l-rose-500" : "border-l-emerald-500";
                return (
                  <button key={`${it.kind}-${it.id}`} data-testid={`agenda-item-${it.id}`} onClick={(e) => { e.stopPropagation(); onItemClick(isoD); }} style={{ top, height }} className={`absolute left-1 right-1 ${kindSt.bg} ${kindSt.text} rounded-sm px-1.5 py-0.5 text-[11px] font-manrope text-left overflow-hidden hover:opacity-80 border-l-2 ${it.kind === "evento" ? estadoLeftBorder : ""}`} title={`${it.titulo} · ${it.hora_inicio}${it.user_name ? " · " + it.user_name : ""}`}>
                    <div className="font-mono text-[10px] opacity-80">{it.hora_inicio}{it.hora_fim ? `–${it.hora_fim}` : ""}</div>
                    <div className="font-semibold truncate">{it.titulo}</div>
                    {isAdmin && it.user_name && <div className="text-[10px] opacity-70 truncate">{it.user_name}</div>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
